import { useEffect, useRef, useState } from 'react';
import { MapPin, PencilRuler, Home, Cpu, Box, BarChart3, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const stepIcons = {
  1: { icon: MapPin, bgImage: '/features/step-location.png' },
  2: { icon: PencilRuler, bgImage: '/features/step-planning.png' },
  3: { icon: Home, bgImage: '/features/step-drawing.png' },
  4: { icon: Cpu, bgImage: '/features/step-2d.png' },
  5: { icon: Box, bgImage: '/features/step-3d.png' },
  6: { icon: BarChart3, bgImage: '/features/step-analysis.png' },
};

export default function Features() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -100px 0px',
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="features"
      ref={sectionRef}
      className="relative py-32 overflow-hidden bg-[#0a0a0f]/30 backdrop-blur-sm"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Section Header */}
        <div className="text-center mb-32 max-w-3xl mx-auto">
          <span className="inline-block px-4 py-1.5 mb-6 text-sm font-bold tracking-wider text-[var(--ds-accent-cyan)] bg-[rgba(var(--accent-cyan-rgb),0.1)] rounded-full uppercase">
            {t.features.badge}
          </span>
          <h2
            className={`text-4xl sm:text-5xl md:text-6xl font-bold text-[var(--ds-text-primary)] mb-8 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
          >
            {t.features.titleLine1} <br />
            <span className="font-serif italic font-normal text-[var(--ds-text-secondary)]">{t.features.titleLine2}</span>
          </h2>
        </div>

        {/* Feature Rows */}
        <div className="space-y-32">
          {t.features.steps.map((step, index) => {
            const isEven = index % 2 === 0;
            const config = stepIcons[step.id as keyof typeof stepIcons];
            const Icon = config.icon;

            return (
              <div
                key={step.id}
                className={`relative flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-12 lg:gap-24 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-24'
                  }`}
                style={{ transitionDelay: `${index * 200}ms` }}
              >
                {/* Connecting Line (Only for non-last steps) */}
                {index < t.features.steps.length - 1 && (
                  <div className={`hidden lg:block absolute top-[50%] ${isEven ? 'left-[50%] translate-x-[-50%]' : 'left-[50%] translate-x-[-50%]'} w-px h-32 bg-gradient-to-b from-[var(--ds-border)] to-transparent -z-10`} />
                )}

                {/* Text Content */}
                <div
                  className="flex-1 text-center lg:text-left group"
                >
                  <div className={`flex items-center gap-4 mb-6 ${isEven ? 'lg:justify-start justify-center' : 'lg:justify-start justify-center'}`}>
                    <div className="w-16 h-16 rounded-2xl bg-[rgba(255,255,255,0.03)] flex items-center justify-center border border-[var(--ds-border)] relative overflow-hidden transition-all duration-500 group-hover:border-[var(--ds-accent-cyan)] group-hover:shadow-[0_0_20px_rgba(var(--accent-cyan-rgb),0.2)]">
                      <div className="absolute inset-0 bg-gradient-to-br from-[var(--ds-accent-purple)] to-[var(--ds-accent-cyan)] opacity-0 group-hover:opacity-20 transition-opacity duration-500" />
                      <Icon className="w-8 h-8 text-[var(--ds-text-primary)] relative z-10 transition-transform duration-500 group-hover:scale-110 group-hover:text-[var(--ds-accent-cyan)]" />
                    </div>
                    <div className="flex flex-col items-start">
                      <span className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[var(--ds-text-primary)] to-[var(--ds-text-secondary)] opacity-20 group-hover:opacity-40 transition-opacity duration-500">
                        {step.step}
                      </span>
                      <span className="text-xs font-bold tracking-[0.2em] text-[var(--ds-accent-cyan)] uppercase mt-1">
                        {step.subtitle}
                      </span>
                    </div>
                  </div>

                  <h3 className="text-3xl md:text-4xl font-bold text-[var(--ds-text-primary)] mb-6 tracking-tight group-hover:text-gradient-purple transition-all duration-300">
                    {step.title}
                  </h3>

                  <p className="text-lg text-[var(--ds-text-secondary)] leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0 group-hover:text-[var(--ds-text-primary)] transition-colors duration-300">
                    {step.description}
                  </p>

                  <button className="group/btn inline-flex items-center gap-2 text-[var(--ds-text-primary)] font-semibold hover:text-[var(--ds-accent-cyan)] transition-colors">
                    <span>{t.features.learnMore}</span>
                    <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
                  </button>
                </div>

                {/* Image/Visual */}
                <div
                  className="flex-1 w-full"
                >
                  <div className="relative rounded-2xl overflow-hidden aspect-video bg-[#1a1a24]/50 border border-[var(--ds-border)] group cursor-pointer transition-all duration-500 hover:shadow-[0_0_30px_rgba(var(--accent-purple-rgb),0.2)] hover:border-[var(--ds-accent-purple)]">
                    <img
                      src={config.bgImage}
                      alt={step.title}
                      className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
                    />

                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
